import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { MerchantStoreDto } from "@/features/merchant/api/dto";
import {
  STORE_STATUSES,
  type StoreStatus,
} from "@/features/shared/api/enums";

export type MerchantStoreRow = MerchantStoreDto & { id: string };

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

const STATUS_VARIANTS: Record<StoreStatus, "success" | "secondary"> = {
  ACTIVE: "success",
  INACTIVE: "secondary",
};

/**
 * Colonnes de la liste des points de vente d'un marchand
 * (`GET /MerchantStores/merchant/{merchantId}`).
 *
 * L'édition est déclenchée au niveau de la page parent (bouton
 * « Modifier » qui pilote la même dialog que la création). Les listes de
 * store n'ont pas de menu d'actions de ligne dédié (colonne actions
 * omise volontairement pour rester alignée avec le reste des sections
 * marchand).
 */
export const merchantStoresColumns: ColumnsClarFactory<MerchantStoreRow> = () => [
  {
    id: "name",
    accessorKey: "name",
    header: t("merchant.stores.columns.name"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "address",
    accessorKey: "address",
    header: t("merchant.stores.columns.address"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "phone",
    accessorKey: "phone",
    header: t("merchant.stores.columns.phone"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("merchant.stores.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as StoreStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: { type: "select", options: selectOptions(STORE_STATUSES) },
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: t("merchant.stores.columns.createdAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        {formatDateWithFallback((getValue() as string) ?? "", "—")}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
];
